import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Upload, Lock, Video, BookOpen, FileText, Trash2, Play, Youtube,
  CheckCircle2, LogOut, Loader2, Music, Mail, HandHeart, Inbox, Pencil, Flame,
} from 'lucide-react';
import { BRAND, HERO_IMAGES } from '../mock';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { toast } from '../hooks/use-toast';
import api, { fileUrl, getToken, setToken } from '../lib/api';
import DosageEditDialog from '../components/DosageEditDialog';

function fmtBytes(n) {
  if (!n && n !== 0) return '';
  const units = ['B', 'KB', 'MB', 'GB'];
  let i = 0;
  while (n >= 1024 && i < units.length - 1) { n /= 1024; i++; }
  return `${n.toFixed(1)} ${units[i]}`;
}

export default function Admin() {
  const [authed, setAuthed] = useState(false);
  const [code, setCode] = useState('');
  const [checking, setChecking] = useState(true);

  const [teachings, setTeachings] = useState([]);
  const [dosages, setDosages] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [prayers, setPrayers] = useState([]);
  const [messages, setMessages] = useState([]);
  const [rsvps, setRsvps] = useState([]);
  const [summary, setSummary] = useState({ prayers_unread: 0, messages_unread: 0 });
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);

  // Teaching form
  const [tTitle, setTTitle] = useState('');
  const [tSeries, setTSeries] = useState('');
  const [tSpeaker, setTSpeaker] = useState('Pastor Oladipo Babalola');
  const [tDuration, setTDuration] = useState('');
  const [tDescription, setTDescription] = useState('');
  const [tVideo, setTVideo] = useState(null);
  const [tAudio, setTAudio] = useState(null);
  const [tThumb, setTThumb] = useState(null);
  const [tPdf, setTPdf] = useState(null);

  // Dosage form
  const [dTitle, setDTitle] = useState('');
  const [dVerseRef, setDVerseRef] = useState('');
  const [dVerse, setDVerse] = useState('');
  const [dBody, setDBody] = useState('');
  const [dMinutes, setDMinutes] = useState('3');
  const [dPostDate, setDPostDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [dImage, setDImage] = useState(null);
  const [editingDosage, setEditingDosage] = useState(null);

  // Material form
  const [mTitle, setMTitle] = useState('');
  const [mDescription, setMDescription] = useState('');
  const [mFile, setMFile] = useState(null);

  useEffect(() => {
    const t = getToken();
    if (!t) { setChecking(false); return; }
    api.verify().then(() => setAuthed(true)).catch(() => setToken(null)).finally(() => setChecking(false));
  }, []);

  useEffect(() => { if (authed) refresh(); }, [authed]);

  const refresh = async () => {
    try {
      const [t, d, m, p, msg, s, r] = await Promise.all([
        api.listTeachings(), api.listDosages(), api.listMaterials(),
        api.adminPrayers().catch(() => []),
        api.adminMessages().catch(() => []),
        api.adminInboxSummary().catch(() => ({ prayers_unread: 0, messages_unread: 0 })),
        api.adminVigilRsvps().catch(() => []),
      ]);
      setTeachings(t); setDosages(d); setMaterials(m);
      setPrayers(p); setMessages(msg); setSummary(s); setRsvps(r);
    } catch (e) {
      toast({ title: 'Could not load', description: String(e?.response?.data?.detail || e.message) });
    }
  };

  const markPrayerRead = async (id) => {
    try {
      await api.markPrayerRead(id);
      setPrayers(prev => prev.map(x => x.id === id ? { ...x, read: true } : x));
      setSummary(s => ({ ...s, prayers_unread: Math.max(0, s.prayers_unread - 1) }));
    } catch (_) {}
  };
  const markMessageRead = async (id) => {
    try {
      await api.markMessageRead(id);
      setMessages(prev => prev.map(x => x.id === id ? { ...x, read: true } : x));
      setSummary(s => ({ ...s, messages_unread: Math.max(0, s.messages_unread - 1) }));
    } catch (_) {}
  };

  const signIn = async (e) => {
    e.preventDefault();
    try {
      const { token } = await api.login(code.trim());
      setToken(token); setAuthed(true);
      toast({ title: 'Welcome, dispenser of grace', description: 'You are signed in.' });
    } catch (err) {
      toast({ title: 'Incorrect passcode', description: 'Please try again.' });
    }
  };

  const signOut = () => { setToken(null); setAuthed(false); };

  const onProgress = (evt) => {
    if (evt.total) setProgress(Math.round((evt.loaded / evt.total) * 100));
  };

  const resetFiles = (formId) => {
    document.querySelectorAll(`#${formId} input[type=file]`).forEach(i => (i.value = ''));
  };

  const uploadTeaching = async (e) => {
    e.preventDefault();
    if (!tTitle) { toast({ title: 'Title is required' }); return; }
    if (!tVideo && !tAudio) { toast({ title: 'Add a video (MP4) or audio (MP3)' }); return; }
    setBusy(true); setProgress(0);
    try {
      const fd = new FormData();
      fd.append('title', tTitle);
      fd.append('series', tSeries || 'Teaching');
      fd.append('speaker', tSpeaker || 'Pastor Oladipo Babalola');
      fd.append('duration', tDuration || '');
      fd.append('description', tDescription || '');
      if (tVideo) fd.append('video', tVideo);
      if (tAudio) fd.append('audio', tAudio);
      if (tThumb) fd.append('thumbnail', tThumb);
      if (tPdf) fd.append('pdf', tPdf);
      await api.createTeaching(fd, onProgress);
      setTTitle(''); setTSeries(''); setTDuration(''); setTDescription('');
      setTVideo(null); setTAudio(null); setTThumb(null); setTPdf(null);
      resetFiles('teaching-form');
      await refresh();
      toast({ title: 'Teaching published', description: 'It is now on the Teachings page.' });
    } catch (err) {
      toast({ title: 'Upload failed', description: String(err?.response?.data?.detail || err.message) });
    } finally { setBusy(false); setProgress(0); }
  };

  const uploadDosage = async (e) => {
    e.preventDefault();
    if (!dTitle || !dBody) { toast({ title: 'Title and body required' }); return; }
    setBusy(true); setProgress(0);
    try {
      const fd = new FormData();
      fd.append('title', dTitle);
      fd.append('verse_ref', dVerseRef || '');
      fd.append('verse', dVerse || '');
      fd.append('body', dBody);
      fd.append('minutes', String(parseInt(dMinutes || '3', 10) || 3));
      if (dPostDate) fd.append('post_date', dPostDate);
      if (dImage) fd.append('image', dImage);
      await api.createDosage(fd, onProgress);
      setDTitle(''); setDVerseRef(''); setDVerse(''); setDBody(''); setDMinutes('3');
      setDPostDate(new Date().toISOString().slice(0, 10));
      setDImage(null);
      resetFiles('dosage-form');
      await refresh();
      toast({ title: 'Dosage published' });
    } catch (err) {
      toast({ title: 'Upload failed', description: String(err?.response?.data?.detail || err.message) });
    } finally { setBusy(false); setProgress(0); }
  };

  const uploadMaterial = async (e) => {
    e.preventDefault();
    if (!mTitle || !mFile) { toast({ title: 'Title and file required' }); return; }
    setBusy(true); setProgress(0);
    try {
      const fd = new FormData();
      fd.append('title', mTitle);
      fd.append('description', mDescription || '');
      fd.append('file', mFile);
      await api.createMaterial(fd, onProgress);
      setMTitle(''); setMDescription(''); setMFile(null);
      resetFiles('material-form');
      await refresh();
      toast({ title: 'Material published' });
    } catch (err) {
      toast({ title: 'Upload failed', description: String(err?.response?.data?.detail || err.message) });
    } finally { setBusy(false); setProgress(0); }
  };

  const remove = async (kind, id) => {
    try {
      if (kind === 'teachings') await api.deleteTeaching(id);
      if (kind === 'dosages') await api.deleteDosage(id);
      if (kind === 'materials') await api.deleteMaterial(id);
      await refresh();
      toast({ title: 'Removed' });
    } catch (err) {
      toast({ title: 'Delete failed', description: String(err?.response?.data?.detail || err.message) });
    }
  };

  if (checking) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-teal-deep" /></div>;
  }

  if (!authed) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-24 pb-16 px-4" style={{ backgroundImage: `linear-gradient(rgba(11,27,39,0.85), rgba(11,27,39,0.85)), url(${HERO_IMAGES.bibleSunrise})`, backgroundSize: 'cover' }}>
        <Card className="w-full max-w-md p-8 bg-white rounded-2xl shadow-2xl">
          <div className="flex items-center gap-3 mb-2">
            <div className="h-11 w-11 rounded-full bg-cream flex items-center justify-center">
              <Lock className="h-5 w-5 text-teal-deep" />
            </div>
            <div>
              <h1 className="font-serif-display text-2xl text-ink">Ministry Studio</h1>
              <p className="text-sm text-ink/60">Enter your passcode to upload</p>
            </div>
          </div>
          <form onSubmit={signIn} className="space-y-4 mt-6">
            <div>
              <Label htmlFor="code">Passcode</Label>
              <Input id="code" type="password" value={code} onChange={(e) => setCode(e.target.value)} className="mt-1.5" placeholder="Enter passcode" autoFocus />
            </div>
            <Button type="submit" className="w-full bg-teal-brand hover:bg-teal-deep text-white h-11">Enter Studio</Button>
            <Link to="/" className="block text-center text-sm text-ink/50 hover:text-teal-deep">Return home</Link>
          </form>
        </Card>
      </div>
    );
  }

  return (
    <div className="pt-24 pb-20 bg-cream min-h-screen">
      <div className="max-w-6xl mx-auto px-4 md:px-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <p className="text-xs tracking-[0.35em] uppercase text-gold-deep mb-2">Ministry Studio</p>
            <h1 className="font-serif-display text-4xl text-ink">Upload &amp; Publish</h1>
            <p className="text-ink/60 mt-1">Everything you publish here goes live instantly on the ministry site.</p>
          </div>
          <div className="flex items-center gap-2">
            <a href={BRAND.youtube} target="_blank" rel="noreferrer"><Button variant="outline" className="h-10"><Youtube className="h-4 w-4 mr-2 text-[#FF0000]" /> YouTube</Button></a>
            <Button variant="outline" className="h-10" onClick={signOut}><LogOut className="h-4 w-4 mr-2" /> Sign out</Button>
          </div>
        </div>

        {busy && (
          <div className="mb-6 p-4 rounded-xl bg-white border border-teal-brand/30 flex items-center gap-3">
            <Loader2 className="h-5 w-5 text-teal-deep animate-spin" />
            <div className="flex-1">
              <p className="text-sm text-ink">Uploading… {progress}%</p>
              <div className="mt-2 h-1.5 rounded-full bg-cream overflow-hidden">
                <div className="h-full bg-gold-brand transition-all" style={{ width: `${progress}%` }} />
              </div>
            </div>
          </div>
        )}

        <Tabs defaultValue="teachings">
          <TabsList className="bg-white border border-ink/10 flex-wrap h-auto">
            <TabsTrigger value="teachings"><Video className="h-4 w-4 mr-2" /> Teachings</TabsTrigger>
            <TabsTrigger value="dosages"><BookOpen className="h-4 w-4 mr-2" /> Daily Dosages</TabsTrigger>
            <TabsTrigger value="materials"><FileText className="h-4 w-4 mr-2" /> Materials</TabsTrigger>
            <TabsTrigger value="inbox" className="relative">
              <Inbox className="h-4 w-4 mr-2" /> Inbox
              {(summary.prayers_unread + summary.messages_unread) > 0 && (
                <span className="ml-2 inline-flex items-center justify-center h-5 min-w-5 px-1.5 rounded-full bg-gold-brand text-ink text-xs font-semibold">
                  {summary.prayers_unread + summary.messages_unread}
                </span>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Teachings */}
          <TabsContent value="teachings" className="mt-6">
            <div className="grid lg:grid-cols-2 gap-6">
              <Card className="p-6 md:p-8 bg-white rounded-2xl border border-ink/5">
                <h2 className="font-serif-display text-2xl text-ink mb-1">Upload a Teaching</h2>
                <p className="text-sm text-ink/60 mb-5">MP4 video, MP3 audio, optional thumbnail &amp; PDF notes.</p>
                <form id="teaching-form" onSubmit={uploadTeaching} className="space-y-4">
                  <div><Label>Title *</Label><Input value={tTitle} onChange={e => setTTitle(e.target.value)} className="mt-1.5" placeholder="Sermon title" /></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Series</Label><Input value={tSeries} onChange={e => setTSeries(e.target.value)} className="mt-1.5" placeholder="e.g. Grace Series" /></div>
                    <div><Label>Duration</Label><Input value={tDuration} onChange={e => setTDuration(e.target.value)} className="mt-1.5" placeholder="38:22" /></div>
                  </div>
                  <div><Label>Speaker</Label><Input value={tSpeaker} onChange={e => setTSpeaker(e.target.value)} className="mt-1.5" /></div>
                  <div><Label>Description</Label><Textarea rows={3} value={tDescription} onChange={e => setTDescription(e.target.value)} className="mt-1.5" /></div>
                  <div><Label>Video (MP4)</Label>
                    <Input type="file" accept="video/mp4,video/*" onChange={e => setTVideo(e.target.files?.[0] || null)} className="mt-1.5" />
                    {tVideo && <p className="text-xs text-ink/50 mt-1">{tVideo.name} · {fmtBytes(tVideo.size)}</p>}
                  </div>
                  <div><Label>Audio (MP3)</Label>
                    <Input type="file" accept="audio/mpeg,audio/mp3,audio/*" onChange={e => setTAudio(e.target.files?.[0] || null)} className="mt-1.5" />
                    {tAudio && <p className="text-xs text-ink/50 mt-1">{tAudio.name} · {fmtBytes(tAudio.size)}</p>}
                  </div>
                  <div><Label>Thumbnail image (optional)</Label>
                    <Input type="file" accept="image/*" onChange={e => setTThumb(e.target.files?.[0] || null)} className="mt-1.5" />
                  </div>
                  <div><Label>Sermon notes PDF (optional)</Label>
                    <Input type="file" accept="application/pdf" onChange={e => setTPdf(e.target.files?.[0] || null)} className="mt-1.5" />
                  </div>
                  <p className="text-xs text-ink/50">You must provide at least a video or an audio file.</p>
                  <Button type="submit" disabled={busy} className="w-full bg-gold-brand hover:bg-[#C48A1F] text-ink font-semibold h-11">
                    {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />} Publish Teaching
                  </Button>
                </form>
              </Card>

              <Card className="p-6 md:p-8 bg-white rounded-2xl border border-ink/5">
                <h2 className="font-serif-display text-2xl text-ink mb-4">Published Teachings ({teachings.length})</h2>
                <div className="space-y-3 max-h-[600px] overflow-y-auto">
                  {teachings.length === 0 && <p className="text-ink/50 italic">No teachings published yet.</p>}
                  {teachings.map(t => (
                    <div key={t.id} className="flex gap-3 p-3 rounded-xl bg-cream/50 border border-ink/5">
                      <div className="h-16 w-24 flex-shrink-0 rounded-lg bg-black overflow-hidden flex items-center justify-center">
                        {t.thumbnail_url ? <img src={fileUrl(t.thumbnail_url)} alt="" className="w-full h-full object-cover" /> : <Play className="h-6 w-6 text-white/70" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-ink truncate">{t.title}</p>
                        <p className="text-xs text-ink/50 truncate">{t.series} · {t.video_url ? 'MP4' : ''}{t.video_url && t.audio_url ? ' + ' : ''}{t.audio_url ? 'MP3' : ''}{t.pdf_url ? ' + PDF' : ''}</p>
                      </div>
                      <button onClick={() => remove('teachings', t.id)} className="text-ink/40 hover:text-red-500 self-center"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  ))}
                </div>
              </Card>
            </div>
          </TabsContent>

          {/* Dosages */}
          <TabsContent value="dosages" className="mt-6">
            <div className="grid lg:grid-cols-2 gap-6">
              <Card className="p-6 md:p-8 bg-white rounded-2xl border border-ink/5">
                <h2 className="font-serif-display text-2xl text-ink mb-4">Publish a Daily Dosage</h2>
                <form id="dosage-form" onSubmit={uploadDosage} className="space-y-4">
                  <div><Label>Title *</Label><Input value={dTitle} onChange={e => setDTitle(e.target.value)} className="mt-1.5" /></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Verse Reference</Label><Input value={dVerseRef} onChange={e => setDVerseRef(e.target.value)} className="mt-1.5" placeholder="John 3:16" /></div>
                    <div><Label>Read time (min)</Label><Input type="number" min="1" max="30" value={dMinutes} onChange={e => setDMinutes(e.target.value)} className="mt-1.5" /></div>
                  </div>
                  <div><Label>Post date</Label><Input type="date" value={dPostDate} onChange={e => setDPostDate(e.target.value)} className="mt-1.5" /></div>
                  <div><Label>Verse text</Label><Textarea rows={2} value={dVerse} onChange={e => setDVerse(e.target.value)} className="mt-1.5" /></div>
                  <div><Label>Devotional body *</Label><Textarea rows={6} value={dBody} onChange={e => setDBody(e.target.value)} className="mt-1.5" /></div>
                  <div><Label>Cover image (optional)</Label>
                    <Input type="file" accept="image/*" onChange={e => setDImage(e.target.files?.[0] || null)} className="mt-1.5" />
                  </div>
                  <Button type="submit" disabled={busy} className="w-full bg-gold-brand hover:bg-[#C48A1F] text-ink font-semibold h-11">
                    {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />} Publish Dosage
                  </Button>
                </form>
              </Card>

              <Card className="p-6 md:p-8 bg-white rounded-2xl border border-ink/5">
                <h2 className="font-serif-display text-2xl text-ink mb-4">Published Dosages ({dosages.length})</h2>
                <div className="space-y-3 max-h-[600px] overflow-y-auto">
                  {dosages.length === 0 && <p className="text-ink/50 italic">No dosages published yet.</p>}
                  {dosages.map(d => (
                    <div key={d.id} className="flex gap-3 p-3 rounded-xl bg-cream/50 border border-ink/5">
                      {d.image_url ? <img src={fileUrl(d.image_url)} className="h-16 w-16 rounded-lg object-cover" alt="" /> : <div className="h-16 w-16 rounded-lg bg-cream flex items-center justify-center"><BookOpen className="h-5 w-5 text-teal-deep" /></div>}
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-ink truncate">{d.title}</p>
                        <p className="text-xs text-ink/50">{d.post_date || (d.created_at ? String(d.created_at).slice(0, 10) : '')} &middot; {d.verse_ref} &middot; {d.minutes} min</p>
                      </div>
                      <button data-testid={`edit-dosage-${d.id}`} onClick={() => setEditingDosage(d)} className="text-ink/40 hover:text-teal-deep self-center" title="Edit"><Pencil className="h-4 w-4" /></button>
                      <button onClick={() => remove('dosages', d.id)} className="text-ink/40 hover:text-red-500 self-center" title="Delete"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  ))}
                </div>
              </Card>
            </div>
          </TabsContent>

          {/* Materials */}
          <TabsContent value="materials" className="mt-6">
            <div className="grid lg:grid-cols-2 gap-6">
              <Card className="p-6 md:p-8 bg-white rounded-2xl border border-ink/5">
                <h2 className="font-serif-display text-2xl text-ink mb-4">Upload Written Material</h2>
                <p className="text-sm text-ink/60 mb-4">PDFs, study guides, articles, and worksheets. Appears in both the Library and Archive.</p>
                <form id="material-form" onSubmit={uploadMaterial} className="space-y-4">
                  <div><Label>Title *</Label><Input value={mTitle} onChange={e => setMTitle(e.target.value)} className="mt-1.5" /></div>
                  <div><Label>Description</Label><Textarea rows={3} value={mDescription} onChange={e => setMDescription(e.target.value)} className="mt-1.5" /></div>
                  <div><Label>File *</Label>
                    <Input type="file" accept=".pdf,.doc,.docx,.txt,application/pdf" onChange={e => setMFile(e.target.files?.[0] || null)} className="mt-1.5" />
                    {mFile && <p className="text-xs text-ink/50 mt-1">{mFile.name} · {fmtBytes(mFile.size)}</p>}
                  </div>
                  <Button type="submit" disabled={busy} className="w-full bg-gold-brand hover:bg-[#C48A1F] text-ink font-semibold h-11">
                    {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />} Publish Material
                  </Button>
                </form>
              </Card>

              <Card className="p-6 md:p-8 bg-white rounded-2xl border border-ink/5">
                <h2 className="font-serif-display text-2xl text-ink mb-4">Library ({materials.length})</h2>
                <div className="space-y-3 max-h-[600px] overflow-y-auto">
                  {materials.length === 0 && <p className="text-ink/50 italic">No materials published yet.</p>}
                  {materials.map(m => (
                    <div key={m.id} className="flex gap-3 p-3 rounded-xl bg-cream/50 border border-ink/5">
                      <div className="h-16 w-16 rounded-lg bg-cream flex items-center justify-center">
                        <FileText className="h-6 w-6 text-teal-deep" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-ink truncate">{m.title}</p>
                        <p className="text-xs text-ink/50">{m.file_name} · {fmtBytes(m.file_size)}</p>
                      </div>
                      <button onClick={() => remove('materials', m.id)} className="text-ink/40 hover:text-red-500 self-center"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  ))}
                </div>
              </Card>
            </div>
          </TabsContent>

          {/* Inbox */}
          <TabsContent value="inbox" className="mt-6">
            <div className="grid lg:grid-cols-2 gap-6">
              <Card className="p-6 md:p-8 bg-white rounded-2xl border border-ink/5">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-serif-display text-2xl text-ink flex items-center gap-2"><HandHeart className="h-5 w-5 text-teal-deep" /> Prayer Requests</h2>
                  {summary.prayers_unread > 0 && (
                    <span className="px-2.5 py-0.5 rounded-full bg-gold-brand text-ink text-xs font-semibold">{summary.prayers_unread} new</span>
                  )}
                </div>
                <div className="space-y-3 max-h-[600px] overflow-y-auto">
                  {prayers.length === 0 && <p className="text-ink/50 italic">No prayer requests yet.</p>}
                  {prayers.map(p => (
                    <div key={p.id} className={`p-4 rounded-xl border ${p.read ? 'bg-white border-ink/10' : 'bg-cream/70 border-gold-brand/40'}`}>
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <div className="flex items-center gap-2 min-w-0">
                          {!p.read && <span className="h-2 w-2 rounded-full bg-gold-brand flex-shrink-0" />}
                          <span className="text-xs tracking-[0.2em] uppercase text-gold-deep font-medium">{p.category}</span>
                        </div>
                        <span className="text-xs text-ink/40 flex-shrink-0">{p.created_at ? new Date(p.created_at).toLocaleDateString() : ''}</span>
                      </div>
                      <p className="font-medium text-ink text-sm">{p.name}{p.email ? ` · ${p.email}` : ''}</p>
                      <p className="text-sm text-ink/70 mt-2 leading-relaxed whitespace-pre-line">{p.message}</p>
                      <div className="mt-3 flex items-center justify-between text-xs text-ink/50">
                        <span>{p.prayers || 0} praying</span>
                        {!p.read && (
                          <button onClick={() => markPrayerRead(p.id)} className="text-teal-deep hover:underline">Mark as read</button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </Card>

              <Card className="p-6 md:p-8 bg-white rounded-2xl border border-ink/5">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-serif-display text-2xl text-ink flex items-center gap-2"><Mail className="h-5 w-5 text-teal-deep" /> Contact Messages</h2>
                  {summary.messages_unread > 0 && (
                    <span className="px-2.5 py-0.5 rounded-full bg-gold-brand text-ink text-xs font-semibold">{summary.messages_unread} new</span>
                  )}
                </div>
                <div className="space-y-3 max-h-[600px] overflow-y-auto">
                  {messages.length === 0 && <p className="text-ink/50 italic">No messages yet.</p>}
                  {messages.map(m => (
                    <div key={m.id} className={`p-4 rounded-xl border ${m.read ? 'bg-white border-ink/10' : 'bg-cream/70 border-gold-brand/40'}`}>
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <div className="flex items-center gap-2 min-w-0">
                          {!m.read && <span className="h-2 w-2 rounded-full bg-gold-brand flex-shrink-0" />}
                          <span className="text-xs tracking-[0.2em] uppercase text-gold-deep font-medium">{m.subject || 'Message'}</span>
                        </div>
                        <span className="text-xs text-ink/40 flex-shrink-0">{m.created_at ? new Date(m.created_at).toLocaleDateString() : ''}</span>
                      </div>
                      <p className="font-medium text-ink text-sm">{m.name} · <a href={`mailto:${m.email}`} className="text-teal-deep hover:underline">{m.email}</a></p>
                      <p className="text-sm text-ink/70 mt-2 leading-relaxed whitespace-pre-line">{m.message}</p>
                      <div className="mt-3 flex items-center justify-end">
                        {!m.read && (
                          <button onClick={() => markMessageRead(m.id)} className="text-xs text-teal-deep hover:underline">Mark as read</button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            </div>

            <Card className="p-6 md:p-8 bg-white rounded-2xl border border-ink/5 mt-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-serif-display text-2xl text-ink flex items-center gap-2"><Flame className="h-5 w-5 text-teal-deep" /> Vigil RSVPs</h2>
                <span data-testid="admin-rsvp-total" className="px-2.5 py-0.5 rounded-full bg-cream text-teal-deep text-xs font-semibold">{rsvps.length} total</span>
              </div>
              {rsvps.length === 0 ? (
                <p className="text-ink/50 italic">No one has RSVP&rsquo;d yet. Share the Home page to invite the saints.</p>
              ) : (
                <div className="space-y-5 max-h-[500px] overflow-y-auto">
                  {Object.entries(rsvps.reduce((acc, r) => {
                    const k = r.vigil_date || 'unknown';
                    (acc[k] = acc[k] || []).push(r);
                    return acc;
                  }, {})).sort((a, b) => b[0].localeCompare(a[0])).map(([date, list]) => (
                    <div key={date}>
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-xs tracking-[0.2em] uppercase text-gold-deep font-semibold">{date}</p>
                        <span className="text-xs text-ink/50">{list.length} {list.length === 1 ? 'saint' : 'saints'}</span>
                      </div>
                      <div className="grid sm:grid-cols-2 gap-2">
                        {list.map(r => (
                          <div key={r.id} className="p-3 rounded-xl bg-cream/50 border border-ink/10">
                            <p className="font-medium text-ink text-sm">{r.name || 'A saint'}</p>
                            {r.email && <a href={`mailto:${r.email}`} className="text-xs text-teal-deep hover:underline break-all">{r.email}</a>}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </TabsContent>
        </Tabs>

        <div className="mt-8 p-5 rounded-xl bg-white border border-gold-brand/30 flex items-start gap-3">
          <CheckCircle2 className="h-5 w-5 text-gold-brand mt-0.5" />
          <p className="text-sm text-ink/70">Published content appears immediately on <Link to="/teachings" className="text-teal-deep underline">Teachings</Link>, <Link to="/audio" className="text-teal-deep underline">Audio</Link>, <Link to="/dosage" className="text-teal-deep underline">Today&rsquo;s Dosage</Link>, <Link to="/materials" className="text-teal-deep underline">Materials</Link>, and the <Link to="/archive" className="text-teal-deep underline">Archive</Link>.</p>
        </div>
      </div>
      <DosageEditDialog
        open={!!editingDosage}
        onOpenChange={(v) => { if (!v) setEditingDosage(null); }}
        dosage={editingDosage}
        onSaved={refresh}
      />
    </div>
  );
}
